import {ActionIcon, Button, Modal, Tooltip} from "@mantine/core";
import React, {useEffect, useMemo, useState} from "react";
import {Attachment, AttachmentTableData} from "../../../../../types/domain";
import {IconEye} from "@tabler/icons-react";
import {MantineReactTable, MRT_ColumnDef, MRT_RowSelectionState, useMantineReactTable} from "mantine-react-table";
import {ShowAttachmentModal} from "./ShowAttachmentModal";
import {loadPreviewItems, PreviewItem} from "../../../../../types/PreviewTypes";
import {formatStages, getFileNameFromPath} from "../helperFunctions";

type ChooseAttachmentsModal = {
    opened: boolean,
    close: () => void,
    attachments: Array<Attachment>
    addAttachmentsToExercise: (attachmentsToAdd: Array<Attachment>) => void,
};

export function ChooseAttachmentsModal({opened, close, addAttachmentsToExercise, attachments}: ChooseAttachmentsModal) {
    const [previewAttachments, setPreviewAttachments] = useState<string[] | null>(null);
    const [previewItems, setPreviewItems] = useState<Array<PreviewItem>>([]);
    const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    /**
     * React to preview interaction and set preview files
     */
    useEffect(() => {
        if (!previewAttachments || previewAttachments.length === 0) return;

        async function load() {
            const items = await loadPreviewItems(previewAttachments!);
            setPreviewItems(items);
        }

        load();
    }, [previewAttachments]);

    useEffect(() => {
        if (attachments.length == 0) {
            addAttachmentsToExercise([]);
            close();
        }
    }, [attachments]);


    /**
     * React to preview-modal for better visibility
     */
    useEffect(() => {
        if (showAttachmentModal) {
            setTimeout(() => {
                window.dispatchEvent(new Event("resize"));
            }, 50);
        }
    }, [showAttachmentModal]);

    const data = useMemo(
        () => {
            let attachmentsTableData: AttachmentTableData[] = [];
            for (let i in attachments) {
                let paths: string[] = [];
                if (attachments[i].path)
                    paths.push(attachments[i].path.replace('"', ""));
                if (attachments[i].solutionPath)
                    paths.push(attachments[i].solutionPath.replace('"', ""));
                if (paths.length > 0)
                    attachmentsTableData.push({
                        id: attachments[i].id,
                        name: getFileNameFromPath(attachments[i].path),
                        solution: attachments[i].solutionPath ? "JA" : "NEIN",
                        topic: attachments[i].topic.name,
                        stages: formatStages(attachments[i].stages),
                        paths: paths
                    })
            }
            return attachmentsTableData;
        }, [attachments]
    );
    const columns = useMemo<MRT_ColumnDef<AttachmentTableData>[]>(
        () =>
            [
                {
                    accessorKey: 'name',
                    header: 'Name',
                },
                {
                    accessorKey: 'solution',
                    header: 'Mit Lösung',
                },
                {
                    accessorKey: 'topic',
                    header: 'Originalthema',
                },
                {
                    accessorKey: 'stages',
                    header: 'Stadien',
                },
                {
                    accessorKey: 'attachments',
                    header: 'Datei',
                    size: 50,
                    mantineTableHeadCellProps: {
                        align: 'center',
                    },
                    mantineTableBodyCellProps: {
                        align: 'center',
                    },
                    Cell: ({row}) => {
                        return (
                            <>
                                <Tooltip label="Datei anzeigen">
                                    <ActionIcon
                                        color="blueish"
                                        onClick={() => {
                                            setPreviewAttachments(row.original.paths);
                                            setShowAttachmentModal(true);
                                        }}>
                                        <IconEye/>
                                    </ActionIcon>
                                </Tooltip>
                            </>
                        )
                    }
                }
            ],
        [],
    );


    const table = useMantineReactTable({
        columns,
        data,
        initialState: {
            pagination: {
                pageSize: 5,
                pageIndex: 0
            }
        },
        enableRowSelection: true,
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        onRowSelectionChange: setRowSelection,
        state: {rowSelection},
        mantinePaginationProps: {
            showRowsPerPage: false
        }
    });

    function addAndClose() {
        let selected: Array<Attachment> = [];
        for (let i in rowSelection) {
            if (rowSelection[i])
                selected.push(attachments[Number.parseInt(i)])
        }
        addAttachmentsToExercise(selected);
        setRowSelection({});
        close();
    }

    return (
        <>
            <Modal
                className="preview-modal"
                opened={opened}
                onClose={close}
                title="Anhang-Auswahl"
                size="xl"
                keepMounted={true}
                withCloseButton={false}
            >
                <MantineReactTable table={table}/>

                <div className="buttonRow">
                    <Button onClick={addAndClose}>
                        {Object.keys(rowSelection).some(k => rowSelection[k])
                            ? "Auswahl speichern"
                            : "Ohne Anhang hinzufügen"}
                    </Button>
                    <Button onClick={() => {
                        setRowSelection({});
                        close()}}
                    >
                        Abbrechen
                    </Button>
                </div>
            </Modal>

            <ShowAttachmentModal opened={showAttachmentModal} close={() => {
                setShowAttachmentModal(false);
                setPreviewAttachments(null);
                setPreviewItems([]);
            }} previewItems={previewItems}/>
        </>
    )
}